import React, { useState, useEffect } from 'react';
import styles from './Dashboard.module.css';

function Dashboard({ onLogout }) {
  const token = localStorage.getItem('token');
  const [mois, setMois] = useState(() => new Date().toISOString().slice(0, 7));
  const [salaire, setSalaire] = useState('');
  const [fraisList, setFraisList] = useState([]);
  const [nouvelleDepense, setNouvelleDepense] = useState({});
  const [chargement, setChargement] = useState(true);

  useEffect(() => {
    const chargerBudget = async () => {
      setChargement(true);
      try {
        const res = await fetch(`/api/budgets/${mois}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (res.status === 401) {
          localStorage.removeItem("token");
          window.location.href = "/login";
          return;
        }

        if (res.ok) {
          const data = await res.json();
          setSalaire(data.salaire);
          setFraisList(data.frais);
        } else {
          setSalaire('');
          setFraisList([]);
        }
      } catch (err) {
        console.error('Erreur chargement', err);
      } finally {
        setChargement(false);
      }
    };

    chargerBudget();
  }, [mois, token]);

  const handleSalaireChange = (e) => setSalaire(e.target.value);

  const handleFraisChange = (index, field, value) => {
    const newList = [...fraisList];
    newList[index][field] = value;
    setFraisList(newList);
  };

  const ajouterFrais = () => {
    setFraisList([...fraisList, { nom: '', montant: '', suivi: false, depenses: [] }]);
  };

  const supprimerFrais = (index) => {
    const newList = [...fraisList];
    newList.splice(index, 1);
    setFraisList(newList);
  };

  const modifierDepense = (fraisIndex, depenseIndex, valeur) => {
    const newList = [...fraisList];
    const montant = parseFloat(valeur);
    if (!isNaN(montant) && montant >= 0) {
      const newDepenses = [...newList[fraisIndex].depenses];
      newDepenses[depenseIndex] = montant;
      newList[fraisIndex].depenses = newDepenses;
      setFraisList(newList);
    }
  };

  const supprimerDepense = (fraisIndex, depenseIndex) => {
    const newList = [...fraisList];
    const newDepenses = [...newList[fraisIndex].depenses];
    newDepenses.splice(depenseIndex, 1);
    newList[fraisIndex].depenses = newDepenses;
    setFraisList(newList);
  };

  const ajouterDepense = (index) => {
    const montant = parseFloat(nouvelleDepense[index]);
    if (isNaN(montant) || montant < 0) return;

    const newList = [...fraisList];
    const oldDepenses = newList[index].depenses || [];
    newList[index].depenses = [...oldDepenses, montant];
    setFraisList(newList);
    setNouvelleDepense({ ...nouvelleDepense, [index]: '' });
  };

  const totalDepenses = fraisList.reduce((sum, frais) => {
    const montant = Number(frais.montant) || 0;
    const depenses = frais.depenses.reduce((s, d) => s + Number(d), 0);
    return sum + (frais.suivi ? Math.max(montant, depenses) : montant);
  }, 0);
  const resteSalaire = Number(salaire) - totalDepenses;

  const totalReel = fraisList.reduce((sum, frais) => {
    const montant = Number(frais.montant) || 0;
    const depenses = frais.depenses.reduce((s, d) => s + Number(d), 0);
    return sum + (frais.suivi ? depenses : montant);
  }, 0);
  const resteReel = Number(salaire) - totalReel;

  const sauvegarderBudget = async () => {
    try {
      const res = await fetch(`/api/budgets/${mois}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ salaire, frais: fraisList }),
      });

      if (res.status === 401) {
        localStorage.removeItem("token");
        window.location.href = "/login";
        return;
      }

      if (!res.ok) {
        const error = await res.json();
        alert(`Erreur sauvegarde : ${error.message}`);
      } else {
        alert('💾 Budget sauvegardé !');
      }
    } catch (err) {
      console.error('Erreur réseau lors de la sauvegarde', err);
      alert('Erreur réseau.');
    }
  };

  const importerMoisPrecedent = async () => {
    const [annee, moisStr] = mois.split('-');
    const moisPrec = String(Number(moisStr) - 1).padStart(2, '0');
    const anneePrec = moisStr === '01' ? String(Number(annee) - 1) : annee;
    const moisPrecedent = moisStr === '01' ? '12' : moisPrec;
    const moisComplet = `${anneePrec}-${moisPrecedent}`;

    try {
      const res = await fetch(`/api/budgets/${moisComplet}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.status === 401) {
        localStorage.removeItem("token");
        window.location.href = "/login";
        return;
      }

      if (res.ok) {
        const data = await res.json();
        const fraisSansDepenses = data.frais.map((f) => ({
          ...f,
          depenses: [],
        }));
        setSalaire(data.salaire);
        setFraisList(fraisSansDepenses);
        alert(`Importé depuis ${moisComplet}`);
      } else {
        alert('Aucun budget trouvé pour le mois précédent.');
      }
    } catch (err) {
      console.error('Erreur import', err);
      alert('Erreur pendant l’import.');
    }
  };

  if (chargement) return <p>Chargement...</p>;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2>Budget mensuel</h2>
        <button onClick={onLogout} className="danger">Se déconnecter</button>
      </div>

      <label>Mois :
        <input
          type="month"
          value={mois}
          onChange={(e) => setMois(e.target.value)}
        />
      </label>

      <label>Salaire :
        <input type="number" value={salaire} onChange={handleSalaireChange} />
      </label><br />
      <b>Reste total à vivre : {isNaN(resteSalaire) ? '---' : resteSalaire.toFixed(2)} € </b><br />
      <b>Reste réel (dépenses suivies) : {isNaN(resteReel) ? '---' : resteReel.toFixed(2)} € </b><br />

      <h3>Frais</h3>

      {fraisList.map((frais, index) => {
        const total = frais.depenses.reduce((sum, d) => sum + Number(d), 0);
        const reste = (Number(frais.montant) || 0) - total;

        return (
          <div className={styles.frais} key={index}>
            <div className={styles.fraisHeader}>
              <input
                type="text"
                placeholder="Nom"
                value={frais.nom}
                onChange={(e) => handleFraisChange(index, 'nom', e.target.value)}
              />
              <input
                type="number"
                placeholder="Montant"
                value={frais.montant}
                onChange={(e) => handleFraisChange(index, 'montant', e.target.value)}
              />
              <label>
                <input
                  type="checkbox"
                  checked={frais.suivi}
                  onChange={(e) => handleFraisChange(index, 'suivi', e.target.checked)}
                />
                Suivi
              </label>
              <button onClick={() => supprimerFrais(index)} className="danger">🗑</button>
            </div>

            {frais.suivi && (
              <>
                <p><strong>Dépenses :</strong> {total.toFixed(2)} € / {frais.montant || '---'} € — Reste : {reste.toFixed(2)} €</p>
                <ul>
                  {frais.depenses.map((val, i) => (
                    <li key={i}>
                      <input
                        type="number"
                        value={val}
                        onChange={(e) => modifierDepense(index, i, e.target.value)}
                      />
                      <button onClick={() => supprimerDepense(index, i)}>Supprimer</button>
                    </li>
                  ))}
                </ul>
                <input
                  type="number"
                  placeholder="Ajouter une dépense"
                  value={nouvelleDepense[index] || ''}
                  onChange={(e) =>
                    setNouvelleDepense({
                      ...nouvelleDepense,
                      [index]: e.target.value,
                    })
                  }
                />
                <button onClick={() => ajouterDepense(index)}>Ajouter</button>
              </>
            )}
          </div>
        );
      })}

      <button onClick={ajouterFrais} className="secondary">+ Ajouter un frais</button>
      <button onClick={importerMoisPrecedent} className="secondary">📥 Importer le mois précédent</button>
      <button onClick={sauvegarderBudget} className="primary">💾 Sauvegarder le mois</button>
       <footer className={styles.footer}>
        Reste total à vivre : {isNaN(resteSalaire) ? '---' : resteSalaire.toFixed(2)} € <br />
        Reste réel (dépenses suivies) : {isNaN(resteReel) ? '---' : resteReel.toFixed(2)} €
      </footer>

    </div>
  );
}

export default Dashboard;
